/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Image,
  Input,
  NativeSelect,
  NativeSelectOption,
  Spinner,
  Textarea,
} from '@repo/shared-client/components/ui'
import { demographicIndicatesMinor } from '@repo/shared-common'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import { useAuth } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import dayjs from 'dayjs'
import { PlayIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AttachedMediaInfo, MediaInfo } from 'src/lib/fragments'
import { UPDATE_MEDIA } from 'src/lib/mutations'
import { videoDurationStr } from 'src/lib/utilsClient'
import type {
  AttachedMediaAdminApproveMutation,
  AttachedMediaAdminApproveMutationVariables,
  AttachedMediaAdminEditQuery,
  AttachedMediaAdminEditQueryVariables,
  AttachedMediaAdminRescanNsfwMutation,
  AttachedMediaAdminRescanNsfwMutationVariables,
  AttachedMediaAdminUpdateAiScoreMutation,
  AttachedMediaAdminUpdateAiScoreMutationVariables,
  MediaAdminApproveMutation,
  MediaAdminApproveMutationVariables,
  MediaAdminEditQuery,
  MediaAdminEditQueryVariables,
  MediaAdminRescanNsfwMutation,
  MediaAdminRescanNsfwMutationVariables,
  MediaAdminUpdateAiScoreMutation,
  MediaAdminUpdateAiScoreMutationVariables,
  NSFWRatingEnum,
} from 'types/graphql'

const formatScanPayload = (t: (key: string) => string, results: unknown) => {
  return results == null ? t('No scanner results') : JSON.stringify(results, null, 2)
}

const MEDIA_ADMIN_EDIT_QUERY: TypedDocumentNode<MediaAdminEditQuery, MediaAdminEditQueryVariables> = gql`
  query MediaAdminEditQuery($id: String!) {
    media(id: $id) {
      ...MediaInfo
      fileSize
      nsfwScan {
        results
      }
      aiDetection {
        results
      }
      demographic {
        ageEstimation
        minorScore
        results
      }
      aiScore
      loveCount
      hahaCount
      wowCount
      droolCount
      hardCount
      naughtyCount
      wetCount
      albumName
    }
  }
  ${MediaInfo}
`

const ATTACHED_MEDIA_ADMIN_EDIT_QUERY: TypedDocumentNode<
  AttachedMediaAdminEditQuery,
  AttachedMediaAdminEditQueryVariables
> = gql`
  query AttachedMediaAdminEditQuery($id: String!) {
    attachedMedia(id: $id) {
      ...AttachedMediaInfo
      fileSize
      nsfwScan {
        results
      }
      aiDetection {
        results
      }
      demographic {
        ageEstimation
        minorScore
        results
      }
      aiScore
    }
  }
  ${AttachedMediaInfo}
`

const APPROVE_MEDIA: TypedDocumentNode<MediaAdminApproveMutation, MediaAdminApproveMutationVariables> = gql`
  mutation MediaAdminApprove($id: String!, $nsfwRating: NSFWRatingEnum!) {
    mediaAdminApprove(id: $id, nsfwRating: $nsfwRating) {
      ...MediaInfo
    }
  }
  ${MediaInfo}
`

const APPROVE_ATTACHED_MEDIA: TypedDocumentNode<
  AttachedMediaAdminApproveMutation,
  AttachedMediaAdminApproveMutationVariables
> = gql`
  mutation AttachedMediaAdminApprove($id: String!, $nsfwRating: NSFWRatingEnum!) {
    attachedMediaAdminApprove(id: $id, nsfwRating: $nsfwRating) {
      ...AttachedMediaInfo
    }
  }
  ${AttachedMediaInfo}
`

const RESCAN_MEDIA: TypedDocumentNode<MediaAdminRescanNsfwMutation, MediaAdminRescanNsfwMutationVariables> = gql`
  mutation MediaAdminRescanNsfw($id: String!) {
    mediaAdminRescanNsfw(id: $id) {
      ...MediaInfo
    }
  }
  ${MediaInfo}
`

const RESCAN_ATTACHED_MEDIA: TypedDocumentNode<
  AttachedMediaAdminRescanNsfwMutation,
  AttachedMediaAdminRescanNsfwMutationVariables
> = gql`
  mutation AttachedMediaAdminRescanNsfw($id: String!) {
    attachedMediaAdminRescanNsfw(id: $id) {
      ...AttachedMediaInfo
    }
  }
  ${AttachedMediaInfo}
`

const UPDATE_MEDIA_AI_SCORE: TypedDocumentNode<
  MediaAdminUpdateAiScoreMutation,
  MediaAdminUpdateAiScoreMutationVariables
> = gql`
  mutation MediaAdminUpdateAiScore($id: String!, $aiScore: Float) {
    mediaAdminUpdateAiScore(id: $id, aiScore: $aiScore) {
      ...MediaInfo
      aiScore
    }
  }
  ${MediaInfo}
`

const UPDATE_ATTACHED_MEDIA_AI_SCORE: TypedDocumentNode<
  AttachedMediaAdminUpdateAiScoreMutation,
  AttachedMediaAdminUpdateAiScoreMutationVariables
> = gql`
  mutation AttachedMediaAdminUpdateAiScore($id: String!, $aiScore: Float) {
    attachedMediaAdminUpdateAiScore(id: $id, aiScore: $aiScore) {
      ...AttachedMediaInfo
      aiScore
    }
  }
  ${AttachedMediaInfo}
`

type MediaAdminEditDialogProps = {
  mediaId: string
  isAttached?: boolean
  open?: boolean
  onOpenChange?: (details: { open: boolean }) => void
  onMediaChange?: (id: string) => void
}

const CAPTION_MAX_LENGTH = 256
const AI_SCORE_MIN = 0
const AI_SCORE_MAX = 1

type Translate = (key: string, options?: { size?: number | string }) => string

const parseAiScoreInput = (value: string): number | null => {
  const trimmed = value.trim()
  if (trimmed === '') return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : Number.NaN
}

const isValidAiScoreInput = (value: string) => {
  const parsed = parseAiScoreInput(value)
  return parsed == null || (parsed >= AI_SCORE_MIN && parsed <= AI_SCORE_MAX)
}

const formatAiScoreInput = (value: number | null | undefined) => (value == null ? '' : String(value))

const formatFileSize = (bytes: number | null | undefined, t: Translate) => {
  if (bytes == null) return t('—')
  if (bytes < 1024) return t('{{size}} B', { size: bytes })
  if (bytes < 1024 * 1024) return t('{{size}} KB', { size: (bytes / 1024).toFixed(1) })
  return t('{{size}} MB', { size: (bytes / (1024 * 1024)).toFixed(1) })
}

const MetadataRow = ({ label, value }: { label: string; value: string | number | null | undefined }) => {
  const { t } = useTranslation()

  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="break-all text-right">{value ?? t('—')}</span>
    </div>
  )
}

const MediaAdminEditDialog = ({
  mediaId,
  isAttached = false,
  open = false,
  onOpenChange,
  onMediaChange,
}: MediaAdminEditDialogProps) => {
  const { t } = useTranslation()
  const { hasPermission } = useAuth()
  const [selectedRating, setSelectedRating] = useState<NSFWRatingEnum>('UNDEFINED')
  const [caption, setCaption] = useState('')
  const [aiScoreInput, setAiScoreInput] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isRescanning, setIsRescanning] = useState(false)
  const [hasSubmittedScan, setHasSubmittedScan] = useState(false)

  const shouldFetchMedia = open && !isAttached && Boolean(mediaId)
  const shouldFetchAttachedMedia = open && isAttached && Boolean(mediaId)
  const canEditCaption = !isAttached && hasPermission('media:update:all')

  const mediaQuery = useQuery(MEDIA_ADMIN_EDIT_QUERY, {
    variables: { id: mediaId },
    skip: !shouldFetchMedia,
    fetchPolicy: 'network-only',
  })

  const attachedMediaQuery = useQuery(ATTACHED_MEDIA_ADMIN_EDIT_QUERY, {
    variables: { id: mediaId },
    skip: !shouldFetchAttachedMedia,
    fetchPolicy: 'network-only',
  })

  const queryResult = isAttached ? attachedMediaQuery : mediaQuery
  const galleryMedia = isAttached ? undefined : mediaQuery.data?.media
  const attachedMedia = isAttached ? attachedMediaQuery.data?.attachedMedia : undefined
  const media = galleryMedia ?? attachedMedia

  useEffect(() => {
    setHasSubmittedScan(false)
    if (!open) {
      setSelectedRating('UNDEFINED')
      setCaption('')
      setAiScoreInput('')
      setIsSaving(false)
      setIsRescanning(false)
    }
  }, [open, mediaId])

  useEffect(() => {
    if (!(open && media?.id)) return
    setSelectedRating(media.nsfwRating)
  }, [open, mediaId, media?.id, media?.nsfwRating])

  useEffect(() => {
    if (!open) return
    setCaption(galleryMedia?.caption ?? '')
  }, [open, mediaId, galleryMedia?.id, galleryMedia?.caption])

  useEffect(() => {
    if (!(open && media?.id)) return
    setAiScoreInput(formatAiScoreInput(media.aiScore))
  }, [open, mediaId, media?.id, media?.aiScore])

  const [updateMedia] = useMutation(UPDATE_MEDIA)
  const [approveMedia] = useMutation(APPROVE_MEDIA)
  const [approveAttachedMedia] = useMutation(APPROVE_ATTACHED_MEDIA)
  const [rescanMedia] = useMutation(RESCAN_MEDIA)
  const [rescanAttachedMedia] = useMutation(RESCAN_ATTACHED_MEDIA)
  const [updateMediaAiScore] = useMutation(UPDATE_MEDIA_AI_SCORE)
  const [updateAttachedMediaAiScore] = useMutation(UPDATE_ATTACHED_MEDIA_AI_SCORE)

  const ratingOptions = [
    { value: 'UNDEFINED', label: t('Unrated') },
    { value: 'SFW', label: t('Safe For Work') },
    { value: 'SUGGESTIVE', label: t('Suggestive') },
    { value: 'EROTICA', label: t('Erotica') },
    { value: 'EXPLICIT', label: t('Explicit') },
  ]

  const originalCaption = galleryMedia?.caption ?? ''
  const captionDirty = canEditCaption && Boolean(galleryMedia) && caption !== originalCaption
  const ratingDirty = Boolean(media) && selectedRating !== media.nsfwRating
  const parsedAiScore = parseAiScoreInput(aiScoreInput)
  const originalAiScore = media?.aiScore ?? null
  const aiScoreDirty = Boolean(media) && parsedAiScore !== originalAiScore
  const aiScoreValid = isValidAiScoreInput(aiScoreInput)
  const isDirty = captionDirty || ratingDirty || aiScoreDirty

  const handleSave = async () => {
    if (!media) return
    if (!isDirty) return

    setIsSaving(true)

    let captionSaved = false
    let ratingSaved = false

    try {
      if (captionDirty) {
        await updateMedia({ variables: { id: mediaId, input: { caption } } })
        captionSaved = true
      }

      if (ratingDirty) {
        try {
          if (isAttached) {
            await approveAttachedMedia({ variables: { id: mediaId, nsfwRating: selectedRating } })
          } else {
            await approveMedia({ variables: { id: mediaId, nsfwRating: selectedRating } })
          }
          ratingSaved = true
        } catch (error) {
          if (captionSaved) {
            toast.error(t('Caption saved, but the NSFW rating could not be updated'))
            onMediaChange?.(mediaId)
            return
          }
          throw error
        }
      }

      if (aiScoreDirty) {
        try {
          if (isAttached) {
            await updateAttachedMediaAiScore({ variables: { id: mediaId, aiScore: parsedAiScore ?? undefined } })
          } else {
            await updateMediaAiScore({ variables: { id: mediaId, aiScore: parsedAiScore ?? undefined } })
          }
        } catch (error) {
          if (captionSaved || ratingSaved) {
            toast.error(t('Some changes were saved, but the AI score could not be updated'))
            onMediaChange?.(mediaId)
            return
          }
          throw error
        }
      }

      toast.success(
        ratingDirty && !captionDirty && !aiScoreDirty
          ? t('NSFW rating updated')
          : aiScoreDirty && !captionDirty && !ratingDirty
            ? t('AI score updated')
            : t('Media update complete')
      )
      onMediaChange?.(mediaId)
      onOpenChange?.({ open: false })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('An error occurred'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleRescan = async () => {
    if (!media || hasSubmittedScan) return

    setHasSubmittedScan(true)
    setIsRescanning(true)

    try {
      if (isAttached) {
        await rescanAttachedMedia({ variables: { id: mediaId } })
      } else {
        await rescanMedia({ variables: { id: mediaId } })
      }

      toast.success(t('NSFW scan submitted'))
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('An error occurred'))
    } finally {
      setIsRescanning(false)
    }
  }

  const reactionTotal = galleryMedia
    ? (galleryMedia.loveCount ?? 0) +
      (galleryMedia.hahaCount ?? 0) +
      (galleryMedia.wowCount ?? 0) +
      (galleryMedia.droolCount ?? 0) +
      (galleryMedia.hardCount ?? 0) +
      (galleryMedia.naughtyCount ?? 0) +
      (galleryMedia.wetCount ?? 0)
    : null

  const ratingValid = !ratingDirty || selectedRating !== 'UNDEFINED'
  const canSave =
    isDirty &&
    ratingValid &&
    aiScoreValid &&
    caption.length <= CAPTION_MAX_LENGTH &&
    !isSaving &&
    !isRescanning &&
    !queryResult.loading
  const nsfwRatingFieldId = `admin-edit-nsfw-rating-${mediaId}`
  const aiScoreFieldId = `admin-edit-ai-score-${mediaId}`
  const captionFieldId = `admin-edit-caption-${mediaId}`

  return (
    <Dialog disablePointerDismissal onOpenChange={(nextOpen) => onOpenChange?.({ open: nextOpen })} open={open}>
      <DialogContent className="flex h-[90dvh] max-h-[90dvh] max-w-3xl flex-col gap-0 overflow-hidden p-0 md:max-w-5xl md:flex-row">
        <div className="relative order-2 mx-4 h-1/2 min-h-0 shrink-0 overflow-hidden rounded-md bg-black md:order-1 md:mx-0 md:h-full md:w-1/2 md:rounded-none">
          {media ? (
            <>
              <Image
                alt={t('Media preview')}
                className="h-full w-full object-contain"
                fallbackSrc="/missing-image.webp"
                src={media.imageUrlMedium ?? media.imageUrlLarge ?? media.imageUrlSmall ?? undefined}
              />
              {media.mediaType === 'VIDEO' ? (
                <PlayIcon
                  aria-hidden
                  className="pointer-events-none absolute top-1/2 left-1/2 w-[20%] -translate-x-1/2 -translate-y-1/2 text-white"
                  fill="white"
                />
              ) : null}
            </>
          ) : null}
        </div>

        <div className="contents md:order-2 md:flex md:h-full md:min-h-0 md:w-1/2 md:flex-col">
          <DialogHeader className="order-1 shrink-0 px-4 pt-4">
            <DialogTitle>{t('Admin Edit Media')}</DialogTitle>
          </DialogHeader>

          {queryResult.loading ? <SFPageLoading className="order-3 py-8" /> : null}
          {queryResult.error ? <ErrorAlert className="order-3 mx-4" error={queryResult.error} /> : null}

          {media ? (
            <div className="order-3 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
              {demographicIndicatesMinor(media.demographic ?? {}) ? (
                <Alert variant="destructive">
                  <AlertTitle>{t('Minor detected')}</AlertTitle>
                  <AlertDescription>
                    {t('An age scan found someone under 18. Review this media before others can see it.')}
                  </AlertDescription>
                </Alert>
              ) : null}
              <div className="grid w-full gap-4 sm:grid-cols-2">
                <div className="flex min-w-0 flex-col gap-1.5">
                  <label className="font-medium text-sm" htmlFor={nsfwRatingFieldId}>
                    {t('NSFW Rating')}
                  </label>
                  <NativeSelect
                    className="w-full min-w-0"
                    id={nsfwRatingFieldId}
                    onChange={(e) => setSelectedRating(e.currentTarget.value as NSFWRatingEnum)}
                    value={selectedRating}
                  >
                    {ratingOptions.map((option) => (
                      <NativeSelectOption key={option.value} value={option.value}>
                        {option.label}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </div>

                <div className="flex min-w-0 flex-col gap-1.5">
                  <label className="font-medium text-sm" htmlFor={aiScoreFieldId}>
                    {t('AI Score (0.000 - 1.000)')}
                  </label>
                  <Input
                    aria-invalid={!aiScoreValid}
                    id={aiScoreFieldId}
                    max={AI_SCORE_MAX}
                    min={AI_SCORE_MIN}
                    onChange={(e) => setAiScoreInput(e.currentTarget.value)}
                    placeholder={t('0.000 to 1.000')}
                    step="0.001"
                    type="number"
                    value={aiScoreInput}
                  />
                </div>
              </div>

              {canEditCaption && galleryMedia ? (
                <div className="flex w-full flex-col gap-1.5">
                  <label className="font-medium text-sm" htmlFor={captionFieldId}>
                    {t('Media Caption')}
                  </label>
                  <Textarea
                    id={captionFieldId}
                    maxLength={CAPTION_MAX_LENGTH}
                    onChange={(e) => setCaption(e.currentTarget.value)}
                    placeholder={t('Media Caption')}
                    rows={3}
                    value={caption}
                  />
                </div>
              ) : null}

              <Accordion className="w-full gap-3" multiple>
                <AccordionItem className="rounded-md border border-border not-last:border-b px-3" value="file-info">
                  <AccordionTrigger className="text-sm">{t('File Info')}</AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-col gap-3">
                      <MetadataRow label={t('Type')} value={media.mediaType} />
                      <MetadataRow label={t('File Name')} value={media.fileName} />
                      {galleryMedia ? <MetadataRow label={t('Album')} value={galleryMedia.albumName} /> : null}
                      <MetadataRow
                        label={t('Dimensions')}
                        value={`${media.width ?? t('—')} × ${media.height ?? t('—')}`}
                      />
                      <MetadataRow label={t('File Size')} value={formatFileSize(media.fileSize, t)} />
                      {media.mediaType === 'VIDEO' ? (
                        <MetadataRow label={t('Duration')} value={videoDurationStr(media.videoDuration)} />
                      ) : null}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem className="rounded-md border border-border not-last:border-b px-3" value="review-info">
                  <AccordionTrigger className="text-sm">{t('Review Info')}</AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-col gap-3">
                      <MetadataRow label={t('Review Status')} value={media.reviewStatus} />
                      <MetadataRow label={t('Reviewer ID')} value={media.reviewerId} />
                      <MetadataRow label={t('Created')} value={dayjs(media.createdAt).format('MMM D, YYYY h:mm A')} />
                      <MetadataRow label={t('Current NSFW Rating')} value={media.nsfwRating} />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem className="rounded-md border border-border not-last:border-b px-3" value="engagement">
                  <AccordionTrigger className="text-sm">{t('Engagement')}</AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-col gap-3">
                      <MetadataRow label={t('Views')} value={media.viewCount} />
                      {galleryMedia ? (
                        <>
                          <MetadataRow label={t('Likes')} value={galleryMedia.likeCount} />
                          <MetadataRow label={t('Comments')} value={galleryMedia.commentCount} />
                          <MetadataRow label={t('Reactions')} value={reactionTotal} />
                        </>
                      ) : null}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem className="rounded-md border border-border not-last:border-b px-3" value="nsfw-scan">
                  <AccordionTrigger className="text-sm">{t('NSFW results')}</AccordionTrigger>
                  <AccordionContent>
                    <pre className="w-full overflow-x-auto whitespace-pre-wrap rounded-md bg-muted p-2 text-sm">
                      {formatScanPayload(t, media?.nsfwScan?.results)}
                    </pre>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem className="rounded-md border border-border not-last:border-b px-3" value="ai-scan">
                  <AccordionTrigger className="text-sm">{t('AI scan results')}</AccordionTrigger>
                  <AccordionContent>
                    <pre className="w-full overflow-x-auto whitespace-pre-wrap rounded-md bg-muted p-2 text-sm">
                      {formatScanPayload(t, media?.aiDetection?.results)}
                    </pre>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  className="rounded-md border border-border not-last:border-b px-3"
                  value="demographic-scan"
                >
                  <AccordionTrigger className="text-sm">{t('Demographics results')}</AccordionTrigger>
                  <AccordionContent>
                    <pre className="w-full overflow-x-auto whitespace-pre-wrap rounded-md bg-muted p-2 text-sm">
                      {formatScanPayload(t, media?.demographic?.results)}
                    </pre>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          ) : null}

          <DialogFooter className="order-4 mx-0 mb-0 shrink-0 flex-row justify-between gap-2 border-border px-4 py-4 sm:justify-between">
            {media?.mediaType === 'IMAGE' ? (
              <Button
                disabled={hasSubmittedScan || isRescanning || isSaving}
                onClick={handleRescan}
                type="button"
                variant="outline"
              >
                {isRescanning ? <Spinner className="size-4" /> : t('Resubmit AI scan')}
              </Button>
            ) : null}
            <div className="ml-auto flex flex-row gap-2">
              <Button onClick={() => onOpenChange?.({ open: false })} type="button" variant="outline">
                {t('Cancel')}
              </Button>
              <Button disabled={!canSave} onClick={handleSave} type="button" variant="default">
                {isSaving ? <Spinner className="size-4" /> : t('Save')}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default MediaAdminEditDialog
